import { useState, useMemo, useRef, useEffect } from 'react'
import {
  ChevronLeft,
  Download,
  RefreshCw,
  Trash2,
  Inbox,
  MoreVertical,
  Sparkles,
  Mail,
} from 'lucide-react'
import type { ReportDetailProps } from './types'
import { QuestionAnswerCard } from './QuestionAnswerCard'
import { GemCard } from './GemCard'
import { CommentThread } from './CommentThread'
import { ReportProcessingState } from './ReportProcessingState'

// Report actions dropdown menu
interface ReportActionsDropdownProps {
  onRegenerate: () => void
  onDelete: () => void
  onSendEmail?: () => void
  onExport?: () => void
  isProcessing: boolean
}

function ReportActionsDropdown({ onRegenerate, onDelete, onSendEmail, onExport, isProcessing }: ReportActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-xl shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
          {!isProcessing && onSendEmail && (
            <button
              onClick={() => {
                onSendEmail()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email report
            </button>
          )}
          {!isProcessing && (
            <button
              onClick={() => {
                onRegenerate()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate
            </button>
          )}
          {!isProcessing && onExport && (
            <button
              onClick={() => {
                onExport()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          )}
          <div className="my-1 border-t border-stone-100 dark:border-stone-700" />
          <button
            onClick={() => {
              onDelete()
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

export function ReportDetail({
  report,
  onShare,
  onExport,
  onRegenerate,
  onDelete,
  onBack,
  onViewItems,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onViewGem,
  onRefresh,
  onViewAllGems,
  onSendEmail,
}: ReportDetailProps) {
  const isProcessing = report.status !== 'completed'
  const formattedDate = new Date(report.createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Track which questions are expanded (by ID)
  const questionIds = useMemo(
    () => report.questionAnswers?.map((qa) => qa.id) ?? [],
    [report.questionAnswers]
  )
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(
    () => new Set(questionIds)
  )

  const allExpanded = expandedQuestions.size === questionIds.length
  const toggleAllQuestions = () => {
    if (allExpanded) {
      setExpandedQuestions(new Set())
    } else {
      setExpandedQuestions(new Set(questionIds))
    }
  }

  const toggleQuestion = (id: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Reports</span>
            </button>
          )}

          {/* Title row with actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Title with report number */}
              <div className="flex items-center gap-3">
                {report.reportNumber && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 text-sm font-medium">
                    #{report.reportNumber}
                  </span>
                )}
                <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
                  {report.title}
                </h1>
              </div>

              {/* Subtitle: stream name, date, items count */}
              <div className="flex items-center gap-2 mt-1.5 text-sm text-stone-500 dark:text-stone-400">
                <button
                  onClick={() => window.location.href = `/streams/${report.streamId}`}
                  className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                >
                  {report.streamName}
                </button>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span>{formattedDate}</span>
                {!isProcessing && report.itemCount > 0 && (
                  <>
                    <span className="text-stone-300 dark:text-stone-600">·</span>
                    <span>{report.itemCount} items analyzed</span>
                  </>
                )}
              </div>
            </div>

            {/* Actions dropdown */}
            <div className="flex items-center gap-3 pt-1">
              <ReportActionsDropdown
                onRegenerate={onRegenerate}
                onDelete={onDelete}
                onSendEmail={onSendEmail}
                onExport={onExport}
                isProcessing={isProcessing}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Consolidated Actions Bar */}
      {!isProcessing && (
        <div className="bg-white dark:bg-stone-900 border-t border-b border-stone-200 dark:border-stone-800">
          <div className="max-w-4xl mx-auto px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {/* Left side buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={onViewItems}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                >
                  <Inbox className="w-4 h-4" />
                  <span>Items</span>
                  <span className="text-stone-400 dark:text-stone-500">({report.itemCount})</span>
                </button>
                {report.gems && report.gems.length > 0 && onViewAllGems && (
                  <button
                    onClick={onViewAllGems}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Gems</span>
                    <span className="text-amber-500 dark:text-amber-500">({report.gems.length})</span>
                  </button>
                )}
              </div>

              {/* Right side buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={onRegenerate}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Regenerate</span>
                </button>
                {onSendEmail && (
                  <button
                    onClick={onSendEmail}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Failed state retry button */}
      {report.status === 'failed' && (
        <div className="bg-white dark:bg-stone-900 border-t border-b border-stone-200 dark:border-stone-800">
          <div className="max-w-4xl mx-auto px-6 py-3">
            <button
              onClick={onRegenerate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Generation
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-4xl mx-auto">
        {/* Show processing state when report is not completed */}
        {isProcessing ? (
          <ReportProcessingState status={report.status} onRefresh={onRefresh} />
        ) : (
          <div className="px-6 py-8 space-y-6">
            {/* Overview */}
            <section>
              <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
                Overview
              </h2>
              <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6">
                <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {report.overview}
                </p>
              </div>
            </section>

            {/* Analysis - all Q&As in one card */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide">
                  Analysis
                </h2>
                {questionIds.length > 0 && (
                  <button
                    onClick={toggleAllQuestions}
                    className="text-xs text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-400 transition-colors"
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                )}
              </div>
              <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6">
                {report.questionAnswers?.map((qa, index) => (
                  <QuestionAnswerCard
                    key={qa.id}
                    qa={qa}
                    isLast={index === (report.questionAnswers?.length ?? 0) - 1}
                    isOpen={expandedQuestions.has(qa.id)}
                    onToggle={() => toggleQuestion(qa.id)}
                  />
                ))}
              </div>
            </section>

            {/* Gems */}
            {report.gems && report.gems.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide">
                      Gems Found
                    </h2>
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-medium">
                      {report.gems.length}
                    </span>
                  </div>
                  {onViewAllGems && (
                    <button
                      onClick={onViewAllGems}
                      className="text-xs text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-400 transition-colors"
                    >
                      View all gems
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {report.gems.map((gem) => (
                    <GemCard
                      key={gem.id}
                      gem={gem}
                      onView={onViewGem ? () => onViewGem(gem.itemId) : undefined}
                      showStatus={false}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Comments */}
            <section className="pt-6 border-t border-stone-200 dark:border-stone-800">
              <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-4">
                Discussion
              </h2>
              <CommentThread
                comments={report.comments ?? []}
                onAddComment={onAddComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
              />
            </section>

            {/* Delivery Log */}
            {report.deliveries && report.deliveries.length > 0 && (
              <section className="pt-6 border-t border-stone-200 dark:border-stone-800">
                <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-4">
                  Email Delivery Log
                </h2>
                <div className="space-y-2">
                  {report.deliveries.map((delivery) => {
                    const deliveredAt = new Date(delivery.deliveredAt)
                    const formattedDate = deliveredAt.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                    const hours = deliveredAt.getHours()
                    const minutes = deliveredAt.getMinutes()
                    const ampm = hours >= 12 ? 'pm' : 'am'
                    const hour12 = hours % 12 || 12
                    const formattedTime = minutes > 0
                      ? `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`
                      : `${hour12}${ampm}`

                    return (
                      <div
                        key={delivery.id}
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-stone-50 dark:bg-stone-800/50"
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-teal-500" />
                          <span className="text-sm text-stone-700 dark:text-stone-300">
                            {delivery.userName}
                          </span>
                          <span className="text-sm text-stone-400 dark:text-stone-500">
                            {delivery.userEmail}
                          </span>
                        </div>
                        <span className="text-xs text-stone-400 dark:text-stone-500">
                          {formattedDate} · {formattedTime}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            {/* AI Generation Info */}
            {report.aiInfo && (
              <footer className="pt-8 mt-8 border-t border-stone-100 dark:border-stone-800/50">
                <p className="text-[10px] text-stone-400 dark:text-stone-600">
                  Generated using {report.aiInfo.model} ({report.aiInfo.totalTokens.toLocaleString()} tokens)
                  {report.aiInfo.aiSource && (
                    <span className="ml-1">
                      · {report.aiInfo.aiSource === 'built-in' ? 'Built-in AI' : 'Your API Key'}
                    </span>
                  )}
                </p>
              </footer>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
